import type { FinancialOverview } from './financial-overview-api';
import { remunerationMoney as money } from './RemunerationColumn';
import { FinanceExplanation } from './FinanceExplanation';

/** Regroupe uniquement les provisions déjà calculées par le domaine. Les
 * montants déclarés sans règlement établi restent dans les points à vérifier,
 * sans être transformés en dette, en zéro ou ajoutés deux fois au total. */
export function ProvisionsSummary({
  source,
}: {
  readonly source: FinancialOverview;
}): React.JSX.Element | null {
  const obligations = source.obligations;
  if (!obligations) return null;
  const groups = [
    { label: 'URSSAF & impôt libératoire', accepts: (id: string) => id.startsWith('social') },
    { label: 'TVA trimestrielle', accepts: (id: string) => id.startsWith('vat') },
    { label: 'Achats fournisseurs', accepts: (id: string) => id.startsWith('suppliers') },
  ];
  const issues = [...new Set(obligations.unresolved)];
  const vat = source.vatPosition;
  return (
    <>
      <div className="provision-proposal__total">
        <span>
          À provisionner
          <small>{obligations.complete ? 'Restant établi' : 'Estimation partielle'}</small>
        </span>
        <strong>
          {obligations.provisionCents === null ? 'À établir' : money(obligations.provisionCents)}
        </strong>
      </div>
      <dl className="provision-summary">
        {groups.map((group) => {
          const entries = obligations.entries.filter((entry) => group.accepts(entry.id));
          const valued = entries.filter((entry) => entry.provisionCents !== null);
          const cents = valued.length
            ? valued.reduce((sum, entry) => sum + BigInt(entry.provisionCents!), 0n).toString()
            : null;
          const partial = valued.length !== entries.length;
          const estimated = valued.some((entry) => entry.estimated);
          return (
            <div key={group.label}>
              <dt>{group.label}</dt>
              <dd>
                <b>{cents === null ? 'À établir' : money(cents)}</b>
                <small>
                  {partial || !entries.length
                    ? 'Partiel · voir le détail'
                    : estimated
                      ? 'Estimé'
                      : 'Restant connu'}
                </small>
              </dd>
            </div>
          );
        })}
        <div className="provision-summary__balance">
          <dt>
            Solde après ces provisions
            <small>
              {obligations.bankDate
                ? `${obligations.bankProvenance ?? 'observé'} au ${obligations.bankDate.split('-').reverse().join('/')}`
                : 'Solde daté du mois requis'}
            </small>
          </dt>
          <dd>
            <b>
              {obligations.availableAfterProvisionsCents === null
                ? 'Solde à compléter'
                : money(obligations.availableAfterProvisionsCents)}
            </b>
          </dd>
        </div>
      </dl>
      <p className="provision-proposal__reserve">
        <span>
          Réserve de sécurité<small>À préserver, sans la reprovisionner chaque mois</small>
        </span>
        <b>{money(source.reserveTargetCents)}</b>
      </p>
      <FinanceExplanation
        title={
          issues.length
            ? `Voir le détail · ${issues.length} point${issues.length > 1 ? 's' : ''} à vérifier`
            : 'Voir le détail'
        }
      >
        <p>
          Le solde après provisions utilise les montants connus et estimés ci-dessus. La réserve de
          sécurité n’est pas encore déduite ; ce solde ne constitue pas une rémunération disponible.
        </p>
        <ul className="provision-summary__details">
          {obligations.entries
            .filter((entry) => entry.remainingCents !== '0')
            .map((entry) => (
              <li key={entry.id}>
                <b>
                  {entry.label} · {entry.period}
                </b>
                <p>
                  {entry.provisionCents !== null
                    ? `Provision : ${money(entry.provisionCents)}.`
                    : entry.amountCents !== null
                      ? `Déclaré : ${money(entry.amountCents)} ; règlement à établir.`
                      : 'Montant à établir.'}{' '}
                  {entry.reason}
                </p>
              </li>
            ))}
        </ul>
        {vat ? (
          <p>
            TVA {vat.period} : collectée {money(vat.collectedCents)} ; TVA d’achats connue{' '}
            {money(vat.knownDeductibleCents)} ; crédit d’ouverture {money(vat.openingCreditCents)}.
            Net estimé {money(vat.estimatedNetCents)} ; réserve prudente{' '}
            {money(vat.prudentProvisionCents)}. {vat.reason}
          </p>
        ) : null}
        {source.purchasePaymentPosition ? (
          <p>
            Fournisseurs : {source.purchasePaymentPosition.outstandingDocuments} achats avec restant
            connu, {source.purchasePaymentPosition.unresolvedDocuments} états à compléter.{' '}
            {source.purchasePaymentPosition.reason}
          </p>
        ) : null}
        <p>
          Une déclaration ne prouve pas son règlement. Les paiements Freebe déjà classés restent
          conservés ; aucune nouvelle saisie de pointage n’est créée.
        </p>
      </FinanceExplanation>
    </>
  );
}
